import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Users, MessageSquare } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import InvestmentProgressBar from './InvestmentProgressBar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Format 12345 -> "12 345" (LV/EU thousands grouping with non-breaking space).
function formatCount(n) {
  return new Intl.NumberFormat('lv-LV').format(n || 0);
}

export default function Footer() {
  const { language, tr } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const year = new Date().getFullYear();
  const [visitorCount, setVisitorCount] = useState(null);

  const handleWriteToAdmin = async () => {
    if (!user) { navigate('/login'); return; }
    try {
      const { data } = await axios.get(`${API}/support/admin-contact`);
      navigate(`/messages?to=${data.id}&name=${encodeURIComponent(data.name)}`);
    } catch (e) {
      navigate('/messages');
    }
  };

  useEffect(() => {
    let cancelled = false;
    // Fire-and-forget: one page-view ping per browser tab boot. The endpoint
    // is idempotent for same-day same-IP+UA visitors, so this is cheap.
    (async () => {
      try {
        const { data } = await axios.post(`${API}/analytics/page-view`);
        if (!cancelled) setVisitorCount(data.unique_visitors);
      } catch (e) {
        // Fall back to read-only stats endpoint if the POST fails.
        try {
          const { data } = await axios.get(`${API}/analytics/site-stats`);
          if (!cancelled) setVisitorCount(data.unique_visitors);
        } catch (_) { /* swallow */ }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const links = [
    { to: '/maksas-un-komisijas', lv: 'Maksas un komisijas', en: 'Fees & Pricing', ru: 'Тарифы и комиссии' },
    { to: '/privatuma-politika', lv: 'Privātuma politika', en: 'Privacy Policy', ru: 'Политика конфиденциальности' },
    { to: '/lietosanas-noteikumi', lv: 'Lietošanas noteikumi', en: 'Terms of Use', ru: 'Условия использования' },
    { to: '/sikdatnu-politika', lv: 'Sīkdatņu politika', en: 'Cookie Policy', ru: 'Политика cookie' },
  ];

  return (
    <footer
      className="mt-16 border-t border-[#E5E4E0] bg-white"
      data-testid="site-footer"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <InvestmentProgressBar />
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-sm text-[#5C5C5C]">
            <span className="font-outfit font-bold text-[#121212]">KoLV</span>
            <span>2026</span>
            <span className="hidden sm:inline">·</span>
            <span className="hidden sm:inline">
              {tr(
                'Latvijas tirgusvieta — vietējiem un labākajiem',
                'Latvian marketplace — local and best',
                'Латвийская торговая площадка — для местных и лучших')}
            </span>
          </div>

          <nav className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="text-[#5C5C5C] hover:text-[#D97736] transition-colors"
                data-testid={`footer-link-${l.to.replace('/', '')}`}
              >
                {tr(l.lv, l.en, l.ru)}
              </Link>
            ))}
            <button
              onClick={handleWriteToAdmin}
              className="inline-flex items-center gap-1.5 text-[#D97736] hover:text-[#C06528] font-medium transition-colors"
              data-testid="footer-write-admin-btn"
            >
              <MessageSquare className="w-4 h-4" />
              {tr('Rakstīt adminam', 'Write to admin', 'Написать администратору')}
            </button>
          </nav>
        </div>

        <div className="mt-6 pt-4 border-t border-[#F0EFEB] text-xs text-[#8A8A8A] text-center space-y-1">
          {visitorCount !== null && (
            <p
              className="flex items-center justify-center gap-1.5 text-[#5C5C5C] mb-2"
              data-testid="visitor-counter"
            >
              <Users className="w-3.5 h-3.5" />
              <span>
                {language === 'lv'
                  ? <>Mūs apmeklējuši <strong className="text-[#D97736]">{formatCount(visitorCount)}</strong> unikāli lietotāji</>
                  : language === 'ru'
                  ? <>Нас посетили <strong className="text-[#D97736]">{formatCount(visitorCount)}</strong> уникальных пользователей</>
                  : <><strong className="text-[#D97736]">{formatCount(visitorCount)}</strong> unique visitors so far</>
                }
              </span>
            </p>
          )}
          <p>
            {tr('Platformas uzturētājs:', 'Operated by:', 'Оператор платформы:')}{' '}
            <span className="text-[#5C5C5C]">SIA “Gatis”</span>,{' '}
            {tr('reģ.nr.', 'reg.no.', 'рег.№')} 50203491851
          </p>
          <p>© {year} KoLV. {tr('Visas tiesības aizsargātas.', 'All rights reserved.', 'Все права защищены.')}</p>
        </div>
      </div>
    </footer>
  );
}
